import useUserVarStore from "@app/store/useUserVarStore";
import downArrow from "@assets/actions/down-arrow.svg";
import AiSummary from "@blocks/AiSummary/AiSummary";
import LatestVideo from "@blocks/LatestVideo/LatestVideo";
import ReactionToolbar from "@blocks/ReactionToolbar/ReactionToolbar";
import ShareSocialButton from "@blocks/ShareSocial/ShareSocialButton";
import ArticleCarousel from "@components/Layouts/Carousel/ArticleCarousel";
import RelatedArticleCarousel from "@components/Layouts/Carousel/RelatedArticleCarousel";
import MainBlock from "@container/MainBlock";
import ArticleParagraph from "@elements/ArticleParagraph/ArticleParagraph";
import { ArticleGaEventsDataProps } from "@elements/ArticleParagraph/ArticleRegwall";
import GetReadingTime from "@elements/ArticleParagraph/GetReadingTime";
import Author from "@elements/Author/Author";
import BeyondWordsPlayer from "@elements/BeyondWordsPlayer/BeyondWordsPlayer";
import Outbrain from "@elements/OutbrainAds/Outbrain";
import { ArticleReactionOptions } from "@elements/Reactions/ArticleReactionOptions";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import { StandFirst } from "@elements/Typography/Paragraph";
import { RenderTags } from "@elements/Typography/Tags";
import { shouldShowTTSPlayer } from "@helper/showTTS";
import { formatDate, getDateTime } from "@helper/useDateFormat";
import { getWindowOrigin, isArrayWithElements } from "@helper/utils";
import useArticleReactions from "@hooks/useArticleReactions";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ArticleAdPageTargetingType,
  ArticleAdSlotTargetingType,
} from "@typings/Ads.d";
import { ReactElement, useState } from "react";
import { twMerge } from "tailwind-merge";

const socialIconList = ["whatsapp", "facebook", "x", "copylink"];

export type ArticleContentCardProps = {
  articleData: ProcessedArticleData;
  isInfiniteScrollArticle?: boolean;
  pageNumber: number;
  articlePageTarget?: ArticleAdPageTargetingType[];
  articleSlotTarget?: ArticleAdSlotTargetingType[];
  hasOutbrain?: boolean;
  gsChannels?: string[];
  isCuePreview?: boolean;
};

export default function ArticleContentCard({
  articleData,
  pageNumber,
  isInfiniteScrollArticle = false,
  articlePageTarget,
  articleSlotTarget,
  hasOutbrain = true,
  gsChannels,
  isCuePreview = false,
}: ArticleContentCardProps): ReactElement {
  const { userToken } = useUserVarStore();
  const {
    id,
    sourceId,
    title,
    sectionName,
    tags,
    authors,
    dateCreated,
    dateEdited,
    kicker,
    articleBody,
    standFirst,
    urlPath,
    imageGallery,
    englishHeadline,
    englishSummary,
    endnote,
    relatedStoriesData,
    datetime,
  } = articleData;

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: id,
    userToken,
    publishDate: datetime,
    uniqueSectionName: sectionName,
  });

  const pageUrl = `${urlPath}`;
  const origin = getWindowOrigin();

  // Get page reading time.
  const readingTime = GetReadingTime(articleBody, urlPath);
  const { isScreenLG } = useWindowSize();
  const [isDateOpen, setDateIsOpen] = useState<boolean>(false);

  // Get count of paragraphs in article body.
  const paraCount = articleBody.reduce(
    (e, element) => (element.type === "paragraph" ? e + 1 : e),
    0,
  );

  // Toggle the dropdown menu
  const toggleDropdown = () => {
    setDateIsOpen((prev) => !prev);
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const tmCustomGaEventsProps = {
    eventLabel: title,
    articleid: id,
    cue_articleid: id,
    contenttype: "1",
    chapter1: "",
    level2: sectionName,
    pubdate: formatDate(dateCreated, "yyyy-MM-dd HH:mm:ss"),
    title: title,
    url: origin + pageUrl,
    keyword: tags ? tags.map((tags) => tags.name).join(", ") : "",
    pagination: pageNumber,
  } as ArticleGaEventsDataProps;

  const showTTSPlayer = shouldShowTTSPlayer(dateCreated);

  return (
    <MainBlock
      showBreadcrumbs={!isInfiniteScrollArticle}
      sectionName={sectionName}
      pageName={title}
      showTitle={false}
    >
      <article className="lg:mt-1">
        <div className="font-primary font-bold text-2sm leading-430 text-black-400">
          {kicker}
        </div>

        <Heading
          className="text-3md lg:text-lg font-bold font-primary text-gray-100"
          sourceId={sourceId}
        >
          {title}
        </Heading>

        <div className="flex flex-col gap-5 lg:gap-0 lg:flex-row justify-between lg:items-center py-5">
          <div className="inline-block">
            <button
              onClick={toggleDropdown}
              className="dropdown-button flex leading-7"
            >
              <Date className="mr-2 text-gray-100" dateTime={dateCreated}>
                {getDateTime(dateCreated)}
              </Date>
              {dateEdited && dateEdited > dateCreated && (
                <img
                  src={downArrow}
                  alt={"dropdown-arrow"}
                  width={10}
                  height={10}
                  className={twMerge(
                    "mt-3 duration-500",
                    isDateOpen && "-rotate-180",
                  )}
                />
              )}
            </button>
            {isDateOpen && dateEdited && dateEdited > dateCreated && (
              <>
                <div className="mb-2 font-primary font-normal text-gray-150 text-xs lg:flex leading-7">
                  <span className="mr-1">மாற்றம் செய்யப்பட்ட நாள்:</span>
                  <Date className="lg:mt-0">{getDateTime(dateEdited)}</Date>
                </div>
              </>
            )}

            <div className="font-primary font-medium text-gray-150 text-xs">
              {readingTime} mins read
            </div>
          </div>

          <div className="flex justify-between lg:justify-center lg:gap-5 items-center">
            <ReactionToolbar
              articleId={id}
              showReactions={showReactions}
              reactions={reactions}
              userReactions={userReactions}
              reactionsError={reactionsError}
              handleReactionClick={handleReactionClick}
            />
            {showReactions && !reactionsError && (
              <div className="hidden lg:block w-[1px] h-[32px] bg-gray-1050"></div>
            )}
            <ShareSocialButton
              socialIconList={socialIconList}
              pagePath={pageUrl}
              mbPopupClassName="absolute right-0"
            />
          </div>
        </div>

        {standFirst && (
          <StandFirst className="border-t border-solid border-gray-2100 mt-4xs mb-5 pt-xxs">
            {standFirst}
          </StandFirst>
        )}
        {showTTSPlayer && (
          <BeyondWordsPlayer
            cueId={id}
            isInfiniteScrollArticle={isInfiniteScrollArticle}
          />
        )}
        {isArrayWithElements(imageGallery) && (
          <div className={`article-carousel-wrapper-${id}`}>
            <ArticleCarousel
              renderItems={imageGallery}
              sectionName={sectionName}
            />
          </div>
        )}

        {isArrayWithElements(authors) && authors.length > 0 && (
          <>
            {authors.map((author, index) => (
              <Author
                key={index}
                destination={author.urlPath ? author.urlPath : ""}
                authorSrc={author.authorImage ? author.authorImage.url : ""}
                authorName={author.name}
              />
            ))}
          </>
        )}

        {englishHeadline && englishSummary && (
          <AiSummary
            englishHeadline={englishHeadline}
            englishSummary={englishSummary}
            customGaEvents={tmCustomGaEventsProps}
          />
        )}

        {isArrayWithElements(articleBody) && (
          <ArticleParagraph
            articleId={id}
            articleTitle={title}
            articleBody={articleBody}
            sectionName={sectionName}
            pageUrl={pageUrl}
            dateCreated={dateCreated}
            tags={tags}
            authors={authors}
            pageNumber={pageNumber}
            isInfiniteScrollArticle={isInfiniteScrollArticle}
            articlePageTarget={articlePageTarget}
            articleSlotTarget={articleSlotTarget}
            gsChannels={gsChannels}
            endnote={endnote}
            isCuePreview={isCuePreview}
            relatedStoriesData={relatedStoriesData && relatedStoriesData}
            bodyParagraphTotal={paraCount}
          />
        )}

        <div className="flex flex-col lg:flex-row gap-5 lg:gap-0 justify-between lg:items-center mt-5 mb-10 lg:my-10">
          <div className="flex flex-col gap-2">
            <div className="text-gray-100 font-semibold font-sm leading-[140%] hidden lg:block">
              இச்செய்தியைப் பகிரவும்
            </div>
            <ShareSocialButton
              socialIconList={socialIconList}
              pagePath={pageUrl}
              mbPopupClassName="mb-5"
              deskClassName="my-2"
            />
          </div>
          <div>
            {showReactions && reactions.length != 0 && (
              <ArticleReactionOptions
                reactions={reactions}
                handleReactionClick={handleReactionClick}
                reactionsUser={userReactions}
                articleId={id}
                variant="bottom"
              />
            )}
          </div>
        </div>

        {/* Tags dont dont render If alias not available in payload*/}
        {isArrayWithElements(tags) && (
          <RenderTags tagsWrapper="mb-5 lg:mb-6" tags={tags} />
        )}

        {relatedStoriesData &&
          isArrayWithElements(relatedStoriesData) &&
          paraCount <= 8 && (
            <RelatedArticleCarousel
              relatedArticles={relatedStoriesData}
              rootClassName="track-impression mt-6 overflow-hidden"
              testId="related-article-carousel"
              dataSectionName="related-story-bottom"
              source={"related-bottom"}
            />
          )}
      </article>

      {/* Video block */}
      {isScreenLG && !isInfiniteScrollArticle && <LatestVideo />}

      {/* Outbrain block */}
      {hasOutbrain && isScreenLG && (
        <div className="border-t border-pink-300 pt-6">
          <Outbrain pathName={pageUrl} version="AR_1" />
        </div>
      )}
    </MainBlock>
  );
}
